import React, { useContext, useState } from 'react'

import { Icon, Image, Header, Button, Dimmer, Modal, Form, Message, Divider } from 'semantic-ui-react'

import QRCode from 'qrcode.react'

import { IProject } from '../../../api/Data'
import { UserContext } from '../../../contexts/UserContext'
import moment from '../../../utils/Moment'
import { convertTextAreaToHTML } from '../../../utils/sharedFunctions'

import ExternalLink from '../../../components/common/ExternalLink'
import UpgradeTag from '../../../components/common/UpgradeTag'
import VideoEncodingCard from '../../../components/common/VideoEncodingCard'

import CollectTabPhoneFrame from './CollectTabPhoneFrame'

const ConsentStatusRow = ({ label, enabled }: { label: string; enabled: boolean }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: '0.75rem',
      marginBottom: '0.35rem',
      fontSize: '12px',
    }}
  >
    <span>{label}</span>
    <strong>{enabled ? '✓' : '✘'}</strong>
  </div>
)
const CollectTabLandingPagePreview = ({
  project,
  handleChange,
}: {
  project: IProject
  handleChange?: (_event: unknown, data: { name: string; value: any; checked?: boolean }) => void
}) => {
  // Contexts
  const { userProfile } = useContext(UserContext)

  // Props
  const {
    title,
    description,
    questions,
    numberOfQuestions,
    deadline,
    incentive,
    rules,
    tips,
    teamTermsUrl,
    imageUrl,
    hasVideo,
    heroVideoEncodingStatus,
    projectUrl,
    allowRecordVideo,
    allowUploadFile,
    allowQrCode,
    locale,
    useTeamLegalSettings,
    overrideContentOwner,
    overridePrivacyUrl,
    overrideLegalDisclosureUrl,
    additionalDeclarationEnabled,
    additionalDeclarationText,
  } = project

  const [showLegalModal, setShowLegalModal] = useState(false)

  const { teamName, logoAvatarUrl } = userProfile!

  const teamProfile = userProfile as any

  const contentOwner =
    useTeamLegalSettings === false && overrideContentOwner ? overrideContentOwner : teamProfile.contentOwner || teamName

  const controllerPrivacyUrl =
    useTeamLegalSettings === false && overridePrivacyUrl ? overridePrivacyUrl : teamProfile.controllerPrivacyUrl

  const legalDisclosureUrl =
    useTeamLegalSettings === false && overrideLegalDisclosureUrl
      ? overrideLegalDisclosureUrl
      : teamProfile.legalDisclosureUrl

  const contributorAdditionalDeclarationEnabled =
    useTeamLegalSettings === false ? additionalDeclarationEnabled : teamProfile.defaultAdditionalDeclarationEnabled

  const contributorAdditionalDeclarationText =
    useTeamLegalSettings === false && additionalDeclarationText
      ? additionalDeclarationText
      : teamProfile.defaultAdditionalDeclarationText

  const { hasBrandedUploadPage } = userProfile.plan

  const projectLinkForQRCode = `${projectUrl}?utm_source=qrcode` // utm is used to differentiate direct form qrcode scan
  const hasProjectRules = !!rules
  const hasAdditionalTerms = !!contributorAdditionalDeclarationEnabled && !!contributorAdditionalDeclarationText
  const projectLanguage = locale

  const handleLegalFieldChange = (name: string, value: any) => {
    if (!handleChange) return

    handleChange(null, {
      name,
      value,
    })
  }

  return (
    <>
      <Header as="h3">
        Preview (<ExternalLink url={project.projectUrl}>view live</ExternalLink>){' '}
        {!hasBrandedUploadPage && (
          <UpgradeTag
            title="Upgrade to add your branding"
            text="Upgrade to the Collector plan to apply your branding to your landing page"
          ></UpgradeTag>
        )}
      </Header>
      <div style={{ width: '100%', maxWidth: '270px', margin: '0 auto' }}>
        <CollectTabPhoneFrame project={project}>
          <>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Image avatar src={logoAvatarUrl} alt="crew" size="large" style={{ width: '30px', height: '30px' }} />
              <Header as="h6" style={{ flex: 1, margin: 0 }}>
                {teamName}
              </Header>

              {!!deadline && (
                <>
                  {moment().isAfter(moment(deadline)) ? (
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <Icon name="calendar times outline" color="black" size="large" />
                      <Header as="h6" style={{ margin: 0 }}>
                        Entries closed
                      </Header>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <Icon name="hourglass half" color="black" size="large" />
                      <div>
                        <Header as="h6" style={{ margin: 0 }}>
                          Entries close
                        </Header>
                        <Header as="h6" className="primaryColor" style={{ margin: 0 }}>
                          {moment(moment(deadline)).format('D MMMM YYYY')}
                        </Header>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {hasVideo && ![200, 199].includes(heroVideoEncodingStatus) ? (
              <VideoEncodingCard status={heroVideoEncodingStatus} />
            ) : (
              <>
                {/* image */}
                <Dimmer.Dimmable
                  style={{
                    width: '100%',
                    height: '138px',
                    display: 'flex',
                    backgroundImage: `url(${imageUrl})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    borderRadius: '20px',
                    margin: '0.5rem 0',
                  }}
                >
                  <Dimmer
                    active={hasVideo}
                    style={{
                      backgroundColor: 'rgba(0,0,0,0.45)',
                      borderRadius: '20px',
                    }}
                  >
                    <Icon name="play circle outline" size="huge" />
                  </Dimmer>
                </Dimmer.Dimmable>
              </>
            )}

            {!!deadline && moment().isAfter(moment(deadline)) && (
              <Header as="h4" className="primaryColor">
                Project closed
              </Header>
            )}

            {/* project image and title */}
            <Header as="h3" style={{ margin: '1rem 0' }}>
              <strong>
                <div dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(title) }} />
              </strong>
            </Header>

            {/* brief */}
            <Header as="h5" style={{ margin: '0.5rem 0' }}>
              <div dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(description || '') }} />
            </Header>

            {!(!!deadline && moment().isAfter(moment(deadline))) && (
              <>
                {numberOfQuestions > 1 && (
                  <Header as="h6">
                    Question 1 of {numberOfQuestions}
                    <Header as="h3" style={{ margin: '0.25rem 0' }}>
                      {questions[0]}
                    </Header>
                  </Header>
                )}

                {allowUploadFile && (
                  <div
                    style={{
                      margin: '1.5rem 0 0.25rem 0',
                      padding: '0.75rem',
                      border: '1px red solid',
                      borderRadius: '4px',
                      textAlign: 'center',
                    }}
                  >
                    <strong>Drag &amp; drop or select from library</strong>
                  </div>
                )}

                {allowRecordVideo && (
                  <Button
                    icon
                    secondary
                    style={{
                      margin: '1.5rem 0 0.25rem 0',
                      padding: '1.5rem 1.25rem',
                      textTransform: 'none',
                    }}
                  >
                    <span style={{ fontSize: '1rem' }}>Click here to record your video</span>
                  </Button>
                )}

                {allowQrCode && (
                  <div style={{ margin: '1.75rem 0', textAlign: 'center' }}>
                    <Header as="h4">
                      <strong>Scan QR code</strong> to open on another device
                    </Header>

                    <QRCode value={projectLinkForQRCode} size={96} bgColor="#ffffff" fgColor="#000000" level="L" />
                  </div>
                )}

                {/* tips */}
                {!!tips && (
                  <Header as="h5">
                    <strong>Filming Tips</strong>
                    <Header as="h5" style={{ margin: '0.25rem 0' }}>
                      <div dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(tips) }} />
                    </Header>
                  </Header>
                )}

                {!!incentive && (
                  <Header as="h5">
                    <strong>Reward</strong>
                    <Header as="h5" style={{ margin: '0.25rem 0' }}>
                      <div dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(incentive) }} />
                    </Header>
                  </Header>
                )}

                {!!rules && (
                  <Header as="h5">
                    <strong>Project Rules</strong>
                    <Header as="h5" style={{ margin: '0.25rem 0' }}>
                      <div dangerouslySetInnerHTML={{ __html: convertTextAreaToHTML(rules) }} />
                    </Header>
                  </Header>
                )}

                {!!teamTermsUrl && (
                  <Header as="h5">
                    <strong>Terms &amp; Conditions</strong>
                    <Header as="h5" style={{ margin: '0.25rem 0' }}>
                      <u>Click here to read the terms and conditions</u>
                    </Header>
                  </Header>
                )}

                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', margin: '2rem 0 0 0' }}>
                  <div style={{ flex: 1 }}>
                    <Header as="h6">
                      <strong className="primaryColor">We just need some details from you</strong>
                    </Header>
                  </div>
                  <Button
                    primary
                    style={{
                      padding: '0.75rem 1rem',
                    }}
                  >
                    Next
                  </Button>
                </div>
              </>
            )}
          </>
        </CollectTabPhoneFrame>

        <div
          style={{
            width: '100%',
            boxSizing: 'border-box',
            margin: '1rem 0 0 0',
            padding: '1rem',
            border: '1px solid #d8d8d8',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <Header as="h5" style={{ margin: '0 0 0.5rem 0', fontWeight: 700 }}>
            Contributor consent legals
          </Header>

          <p style={{ margin: '0 0 0.75rem 0', fontSize: '12px' }}>
            Configure the legal terms your contributors will view and agree to when making a submission.
          </p>

          <div style={{ marginBottom: '0.75rem' }}>
            <strong style={{ fontSize: '12px' }}>Your legal details</strong>
          </div>

          <ConsentStatusRow label={`Legal entity: ${contentOwner || 'Missing'}`} enabled={!!contentOwner} />
          <ConsentStatusRow label="Your Privacy Policy" enabled={!!controllerPrivacyUrl} />
          <ConsentStatusRow label="Your legal disclosure" enabled={!!legalDisclosureUrl} />

          <div style={{ margin: '0.9rem 0 0.75rem 0' }}>
            <strong style={{ fontSize: '12px' }}>Project legals</strong>
          </div>

          <ConsentStatusRow label={`Project team name: ${teamName || 'Missing'}`} enabled={!!teamName} />
          <ConsentStatusRow label={`Project language: ${projectLanguage}`} enabled={!!projectLanguage} />
          <ConsentStatusRow label="Additional terms" enabled={hasAdditionalTerms} />
          <ConsentStatusRow label="Your project rules" enabled={hasProjectRules} />
          <ConsentStatusRow label="Your Terms & Conditions" enabled={!!teamTermsUrl} />

          <div style={{ margin: '0.9rem 0 0.75rem 0' }}>
            <strong style={{ fontSize: '12px' }}>Platform legals</strong>
          </div>

          <ConsentStatusRow
            label={
              projectLanguage
                ? `Platform Privacy Notice: ${projectLanguage}`
                : 'Platform Privacy Notice: language not set'
            }
            enabled
          />
          <ConsentStatusRow label="Vloggi Platform Terms" enabled />
          <ConsentStatusRow label="Content Assignment Agreement" enabled />

          <Button basic compact fluid style={{ marginTop: '0.75rem' }} onClick={() => setShowLegalModal(true)}>
            Edit consent settings
          </Button>
        </div>
        <Modal open={showLegalModal} onClose={() => setShowLegalModal(false)} size="small">
          <Modal.Header>Contributor consent settings</Modal.Header>

          <Modal.Content scrolling>
            <div
              style={{
                background: '#f7f7f7',
                border: '1px solid #e5e5e5',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
              }}
            >
              <Header as="h4" style={{ margin: '0 0 0.35rem 0' }}>
                Contributor consent settings
              </Header>
              <p style={{ margin: 0, fontSize: '13px' }}>
                These settings control what contributors are told before they upload. Vloggi platform legal terms are
                included automatically.
              </p>
            </div>

            <Form>
              <Header as="h4">Your legal details</Header>
              <p style={{ fontSize: '13px', marginTop: '-0.25rem' }}>
                These identify the organisation that owns or controls the submitted content.
              </p>

              <Form.Input
                label="Legal entity / content owner"
                name="overrideContentOwner"
                value={overrideContentOwner || contentOwner || ''}
                placeholder="Example: Vloggi Pty Ltd"
                onChange={(_event, data) => {
                  handleLegalFieldChange('useTeamLegalSettings', false)
                  handleLegalFieldChange('overrideContentOwner', data.value)
                }}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                The organisation that contributors assign their content rights to.
              </p>

              <Form.Input
                label="Your Privacy Policy URL"
                name="overridePrivacyUrl"
                value={overridePrivacyUrl || controllerPrivacyUrl || ''}
                placeholder="Example: https://example.com/privacy"
                onChange={(_event, data) => {
                  handleLegalFieldChange('useTeamLegalSettings', false)
                  handleLegalFieldChange('overridePrivacyUrl', data.value)
                }}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Add your organisation’s privacy policy so contributors know how you handle personal data.
              </p>

              <Form.Input
                label="Your legal disclosure URL"
                name="overrideLegalDisclosureUrl"
                value={overrideLegalDisclosureUrl || legalDisclosureUrl || ''}
                placeholder="Example: https://example.com/legal"
                onChange={(_event, data) => {
                  handleLegalFieldChange('useTeamLegalSettings', false)
                  handleLegalFieldChange('overrideLegalDisclosureUrl', data.value)
                }}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Use this for an Impressum, legal notice, corporate disclosure page or equivalent.
              </p>

              <Divider />

              <Header as="h4">Project legals</Header>
              <p style={{ fontSize: '13px', marginTop: '-0.25rem' }}>
                These apply only to this project and are shown exactly as written.
              </p>

              <Form.Select
                label="Project language"
                name="locale"
                value={locale || ''}
                placeholder="Select language"
                options={[
                  { key: 'en-gb', text: 'English UK', value: 'en-gb' },
                  { key: 'en-us', text: 'English US', value: 'en-us' },
                  { key: 'fr', text: 'French', value: 'fr' },
                  { key: 'es', text: 'Spanish', value: 'es' },
                  { key: 'de', text: 'German', value: 'de' },
                  { key: 'it', text: 'Italian', value: 'it' },
                  { key: 'nl', text: 'Dutch', value: 'nl' },
                  { key: 'pt-br', text: 'Portuguese Brazil', value: 'pt-br' },
                ]}
                onChange={(_event, data) => handleLegalFieldChange('locale', data.value)}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Controls the upload page language and which Vloggi privacy notice contributors see.
              </p>

              <Form.TextArea
                label="Additional terms"
                name="additionalDeclarationText"
                value={additionalDeclarationText || ''}
                placeholder="Example: I confirm this content is my own original work and that everyone appearing has given permission."
                onChange={(_event, data) => {
                  handleLegalFieldChange('additionalDeclarationEnabled', !!data.value)
                  handleLegalFieldChange('additionalDeclarationText', data.value)
                }}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Optional extra confirmation shown as an additional contributor checkbox.
              </p>

              <Form.TextArea
                label="Project rules"
                name="rules"
                value={rules || ''}
                placeholder="Example: Videos must be filmed during the event and must not include confidential information."
                onChange={(_event, data) => handleLegalFieldChange('rules', data.value)}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Free-text rules shown on the landing page. Add translated rules yourself if the project is multilingual.
              </p>

              <Form.Input
                label="Terms & Conditions URL"
                name="teamTermsUrl"
                value={teamTermsUrl || ''}
                placeholder="Example: https://example.com/terms"
                onChange={(_event, data) => handleLegalFieldChange('teamTermsUrl', data.value)}
              />
              <p style={{ fontSize: '12px', marginTop: '-0.5rem' }}>
                Optional project terms link shown to contributors.
              </p>

              <Divider />

              <Header as="h4">Platform legals</Header>
              <Message>
                <p>
                  Vloggi automatically includes the{' '}
                  <a href="https://vloggi.com/content-assignment-agreement/" target="_blank" rel="noopener noreferrer">
                    Content Assignment Agreement
                  </a>
                  ,{' '}
                  <a href="https://vloggi.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
                    Vloggi Platform Privacy Notice
                  </a>{' '}
                  and{' '}
                  <a href="https://vloggi.com/terms-and-conditions/" target="_blank" rel="noopener noreferrer">
                    Vloggi Platform Terms
                  </a>
                  .
                </p>
                <p style={{ marginBottom: 0 }}>
                  The Vloggi Platform Privacy Notice is available in multiple languages and follows the project language
                  where supported.
                </p>
              </Message>
            </Form>
          </Modal.Content>

          <Modal.Actions>
            <Button onClick={() => setShowLegalModal(false)}>Done</Button>
          </Modal.Actions>
        </Modal>
      </div>
    </>
  )
}
export default CollectTabLandingPagePreview
